import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Cpu, Workflow, BrainCircuit, Server, Scale, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api, mediaUrl } from "../lib/api";
import { Seo } from "../components/Seo";
import BrandGrid from "../components/BrandGrid";
import NeuralBackground from "../components/NeuralBackground";
import CtaEyes from "../components/CtaEyes";
import HumanTechMosaic from "../components/HumanTechMosaic";

const PILLARS = [
  { icon: Cpu, k: "pillar_tech", d: "pillar_tech_d" },
  { icon: Workflow, k: "pillar_auto", d: "pillar_auto_d" },
  { icon: BrainCircuit, k: "pillar_ai", d: "pillar_ai_d" },
  { icon: Server, k: "pillar_infra", d: "pillar_infra_d" },
];

export default function Home() {
  const { t, tr } = useLang();
  const [banners, setBanners] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/public/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/public/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo page="home" />
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden" data-testid="hero-section">
        <Swiper modules={[Autoplay, Pagination]} autoplay={{ delay: 6000 }} pagination={{ clickable: true }}
          loop={banners.length > 1} className="h-full">
          {(banners.length ? banners : [null]).map((b, i) => (
            <SwiperSlide key={b?._id || i}>
              <div className="relative h-full w-full">
                {b?.image_desktop && (
                  <img src={mediaUrl(b.image_desktop)} alt={tr(b.title)} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/30" />
                <div className="hero-orb w-[600px] h-[600px] -right-40 -top-40 opacity-60" />
                <div className="hero-orb w-[400px] h-[400px] -right-16 -top-16 opacity-40" style={{ animationDirection: "reverse" }} />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-center">
                  <span className="font-mono-tech text-xs tracking-[0.3em] text-[#00F0FF] mb-6 fade-up">NATUR COMMODITIES // 2035 READY</span>
                  <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-tight fade-up text-white" data-testid="hero-title">
                    {b ? tr(b.title) : "OPERADOR DIGITAL CORPORATIVO"}
                  </h1>
                  <p className="text-zinc-300 max-w-xl mt-6 text-sm sm:text-base fade-up">{b ? tr(b.subtitle) : ""}</p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    {b?.url && (
                      <Link to={b.url} data-testid="hero-cta-btn"
                        className="inline-flex items-center gap-2 bg-[#5698C7] text-white text-sm font-semibold rounded-md px-7 py-3.5 hover:bg-[#4587B8] transition-colors duration-200 fade-up">
                        {tr(b.cta) || t("hero_default_cta")} <ArrowRight size={15} />
                      </Link>
                    )}
                    <Link to="/contacto" data-testid="hero-secondary-cta"
                      className="inline-flex items-center gap-2 border border-white/40 text-white text-sm font-semibold rounded-md px-7 py-3.5 hover:bg-white/10 hover:border-white transition-colors duration-200 fade-up">
                      {t("nav_contact")}
                    </Link>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      <section className="relative z-20 -mt-16 px-4 sm:px-6" data-testid="stats-band">
        <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-xl border border-slate-200 grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100 overflow-hidden">
          {[["8+", t("stat_brands")], ["24/7", t("stat_ops")], ["100%", t("stat_central")], ["10+", t("stat_platforms")]].map(([v, l], i) => (
            <div key={i} className="p-8 text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-3xl sm:text-4xl font-bold text-[#0B1220]">{v}</div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="pillars-section">
        <NeuralBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <span className="font-mono-tech text-xs tracking-[0.3em] text-[#5698C7]">CORE</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-light mt-3 mb-12">{t("tech_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map((p) => (
              <div key={p.k} className="hud-border p-8 group overflow-hidden pillar-card" data-testid={`pillar-${p.k}`}
                style={{ backgroundImage: `url(${mediaUrl("/api/uploads/texture-teal.png")})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                <div className="w-12 h-12 rounded-lg bg-[#F6F3EC]/15 flex items-center justify-center mb-6">
                  <p.icon size={22} strokeWidth={1.5} className="text-[#F6F3EC]" />
                </div>
                <h3 className="font-display text-base font-semibold mb-3 text-[#F6F3EC]">{t(p.k)}</h3>
                <p className="text-xs text-[#F6F3EC] leading-relaxed">{t(p.d)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="ecosystem-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="font-mono-tech text-xs tracking-[0.3em] text-[#5698C7]">ECOSISTEMA</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-light mt-3">{t("eco_title")}</h2>
          </div>
          <Link to="/ecosistema" className="hidden sm:flex items-center gap-2 text-xs font-mono-tech text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid="ecosystem-view-all">
            VER TODO <ArrowRight size={12} />
          </Link>
        </div>
        <BrandGrid brands={brands.slice(0, 8)} />
      </section>

      <section className="border-t border-slate-200 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Infraestructura tecnológica" className="rounded-xl shadow-xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-[#5698C7] text-white rounded-xl shadow-xl px-8 py-6">
              <div className="font-display text-2xl font-bold">2035</div>
              <div className="text-[11px] uppercase tracking-wide text-blue-100">Ready</div>
            </div>
          </div>
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5698C7]">{t("why_overline")}</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mt-3 leading-tight">{t("why_title")}</h2>
            <p className="text-slate-500 mt-4 max-w-xl">{t("why_sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="flex items-start gap-3" data-testid={`why-item-${n}`}>
                  <CheckCircle2 size={18} className="text-[#5698C7] mt-0.5 shrink-0" strokeWidth={2} />
                  <span className="text-sm text-slate-700">{t(`why_${n}`)}</span>
                </div>
              ))}
            </div>
            <Link to="/operador-digital" data-testid="why-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#5698C7] text-white text-sm font-semibold rounded-md px-7 py-3.5 hover:bg-[#4587B8] transition-colors duration-200">
              {t("why_cta")} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="hud-border p-8 relative overflow-hidden glow-blue pillar-card" data-testid="legal-teaser"
            style={{ backgroundImage: `url(${mediaUrl("/api/uploads/texture-teal.png")})`, backgroundSize: "cover", backgroundPosition: "center" }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-[#F6F3EC]/15 flex items-center justify-center shrink-0">
                <Scale size={22} strokeWidth={1.5} className="text-[#F6F3EC]" />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F6F3EC]">{t("legal_teaser_title")}</h2>
            </div>
            <p className="text-sm text-[#F6F3EC] mb-5 max-w-md">{t("legal_teaser_sub")}</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {[t("legal_chip_versions"), t("legal_chip_snapshots"), t("legal_chip_hash"), t("legal_chip_history")].map((c) => (
                <span key={c} className="text-[11px] font-medium bg-[#F6F3EC]/15 text-[#F6F3EC] rounded-full px-3 py-1">{c}</span>
              ))}
            </div>
            <Link to="/legal" data-testid="legal-teaser-cta" className="inline-flex items-center gap-2 text-xs font-mono-tech tracking-widest text-[#F6F3EC] hover:opacity-70 transition-opacity duration-200">
              {t("legal_teaser_cta").toUpperCase()} <ArrowRight size={12} />
            </Link>
          </div>
          <div className="hud-border p-8 relative overflow-hidden pillar-card" data-testid="intranet-teaser"
            style={{ backgroundImage: `url(${mediaUrl("/api/uploads/texture-teal.png")})`, backgroundSize: "cover", backgroundPosition: "center" }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-[#F6F3EC]/15 flex items-center justify-center shrink-0">
                <Lock size={22} strokeWidth={1.5} className="text-[#F6F3EC]" />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F6F3EC]">{t("intranet_teaser_title")}</h2>
            </div>
            <p className="text-sm text-[#F6F3EC] mb-5 max-w-md">{t("intranet_teaser_sub")}</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {[t("intranet_announcements"), t("intranet_docs"), t("intranet_links")].map((c) => (
                <span key={c} className="text-[11px] font-medium bg-[#F6F3EC]/15 text-[#F6F3EC] rounded-full px-3 py-1">{c}</span>
              ))}
            </div>
            <Link to="/login" data-testid="intranet-teaser-cta" className="inline-flex items-center gap-2 text-xs font-mono-tech tracking-widest text-[#F6F3EC] hover:opacity-70 transition-opacity duration-200">
              {t("intranet_teaser_cta").toUpperCase()} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      <HumanTechMosaic />

      <section className="bg-gradient-to-br from-[#1F91AE] via-[#15718F] to-[#0E5471] relative overflow-hidden" data-testid="cta-band">
        <NeuralBackground density={33} color="190,225,255" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-[#034343] font-display text-2xl sm:text-3xl font-bold max-w-2xl leading-tight">{t("cta_title")}</h2>
            <p className="text-[#F8F3E8] mt-3 max-w-xl text-sm">{t("cta_sub")}</p>
          </div>
          <div className="flex flex-col items-center gap-4 shrink-0">
            <CtaEyes />
            <Link to="/contacto" data-testid="cta-band-btn"
              className="bg-[#F4F5F7] text-[#5698C7] font-semibold text-sm rounded-md px-8 py-4 hover:bg-[#0B1220] hover:text-white transition-colors duration-200 shrink-0">
              {t("cta_btn")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
